import isEmpty from "lodash/isEmpty";
import keys from "lodash/keys";
import values from "lodash/values";
import { cameraService } from "client-app-core";

/**
 * utils is a singleton that gets defined through an IIFE (Immediately Invoked Function Expression)
 * so that it runs only once when the module is loaded. Its purpose is to download client-side scripts
 * needed for the VICADS video player and inject them into the HTML; fetch, cache and provide tokens
 * to individual video player instances, and refresh those tokens at regular intervals to prevent
 * login expiration.
 */
const utils = (() => {
	const urlTokenSetters = {}; // tokens fetched from client-side using camera's playerSessionUrl setting
	const svcTokenSetters = {}; // tokens fetched from camera service.
	const TOKEN_EXPIRATION_SECONDS = 600;
	let scriptsLoadStatus = "NOT_LOADED";
	let numScriptsLoaded = 0;

	const injectVicadsScriptIntoRootHtml = async (url, type = "", id) => {
		try {
			const response = await fetch(url, {
				method: "GET"
			});

			if (!response.ok) {
				throw new Error("Network response was not ok " + response.statusText);
			}

			const scriptContent = await response.text();

			const script = document.createElement("script");
			script.id = id;
			script.textContent = scriptContent;
			if (type !== "") {
				script.type = type;
			}
			document.body.appendChild(script);
		} catch (error) {
			console.error("There was a problem with fetching or appending the script:", error);
			throw error; // Rethrow the error to ensure the calling function also fails
		}
	};

	const scriptUrls = [
		"/_fileDownload/vicads-video/polyfills.js",
		"/_fileDownload/vicads-video/adapter.js",
		"/_fileDownload/vicads-video/kurento-utils.min.js",
		"/_fileDownload/vicads-video/main.js"
	];

	const loadScripts = async () => {
		scriptsLoadStatus = "LOADING";
		try {
			for (let i = numScriptsLoaded; i < scriptUrls.length; i++) {
				await injectVicadsScriptIntoRootHtml(
					scriptUrls[i],
					"module",
					`vicads-${["polyfills", "adapter", "kurento", "main"][i]}`
				);
				numScriptsLoaded++;
			}

			scriptsLoadStatus = "LOADED";
			refreshTokens();
			setInterval(refreshTokens, TOKEN_EXPIRATION_SECONDS * 1000);
		} catch (error) {
			// We will try to resume loading from the point of failure when the next token setter
			// registration request is made.
			scriptsLoadStatus = "NOT_LOADED";
			console.error("Error loading scripts:", error);
		}
	};

	const refreshToken = async (url, cameraId) => {
		const tokenSettersConfig = url ? urlTokenSetters[url] : svcTokenSetters[cameraId];
		if (isEmpty(tokenSettersConfig.registrations)) {
			if (new Date() - tokenSettersConfig.fetchTime > TOKEN_EXPIRATION_SECONDS) {
				// Token expired and no registrations exist, we remove this entry.
				if (url) {
					delete urlTokenSetters[url];
				} else {
					delete svcTokenSetters[cameraId];
				}
			}
			return;
		}
		fetchAndApplyToken(url, cameraId);
	};

	const refreshTokens = async () => {
		if (scriptsLoadStatus !== "LOADED") {
			return;
		}
		const urls = keys(urlTokenSetters);
		for (let i = 0; i < urls.length; i++) {
			const url = urls[i];
			await refreshToken(url, null);
		}
		const cameraIds = keys(svcTokenSetters);
		for (let i = 0; i < cameraIds.length; i++) {
			const cameraId = cameraIds[i];
			await refreshToken(null, cameraId);
		}
	};

	const fetchAndApplyToken = async (url, cameraId) => {
		const tokenSettersConfig = url ? urlTokenSetters[url] : svcTokenSetters[cameraId];
		if (tokenSettersConfig.status === "TOKEN_FETCHING") {
			return;
		}
		tokenSettersConfig.status = "TOKEN_FETCHING";
		let token;
		try {
			token = await new Promise(resolve => {
				if (url) {
					const xhttp = new XMLHttpRequest();
					xhttp.onreadystatechange = () => {
						if (xhttp.readyState === 4) {
							if (xhttp.status === 200) {
								const response = xhttp.responseText;
								resolve(response);
							} else {
								resolve("");
							}
						}
					};
					xhttp.open("GET", url, true);
					xhttp.send();
				} else {
					cameraService.getPlayerSession(cameraId, {}, (err, response) => {
						if (err) {
							console.log("ERROR", err);
							resolve("");
							return;
						}
						if (response?.access_token) {
							resolve(response.access_token);
						} else {
							resolve("");
						}
					});
				}
			});
		} catch (error) {
			token = "";
			console.error("Error fetching token:", error);
		}

		tokenSettersConfig.status = "TOKEN_FETCHED";
		if (tokenSettersConfig.token && token === "") {
			// An old token already exists and a new one could not be obtained, we keep using the old one.
			return;
		}
		tokenSettersConfig.token = token;
		tokenSettersConfig.fetchTime = new Date();
		values(tokenSettersConfig.registrations).forEach(tokenSetter => tokenSetter(token));
	};

	const registerTokenSetter = (id, camera, setToken) => {
		if (scriptsLoadStatus === "NOT_LOADED") {
			loadScripts();
		}

		let tokenSettersConfig;
		if (camera.player?.playerSessionUrl) {
			const url = camera.player.playerSessionUrl;
			if (!Object.hasOwn(urlTokenSetters, url)) {
				urlTokenSetters[url] = { registrations: {}, status: "INIT" };
			}
			tokenSettersConfig = urlTokenSetters[url];
		} else {
			if (!Object.hasOwn(svcTokenSetters, camera.id)) {
				svcTokenSetters[camera.id] = { registrations: {}, status: "INIT" };
			}
			tokenSettersConfig = svcTokenSetters[camera.id];
		}
		tokenSettersConfig.registrations[id] = setToken;
		if (tokenSettersConfig.token) {
			setToken(tokenSettersConfig.token);
		} else if (scriptsLoadStatus === "LOADED" && tokenSettersConfig.status === "INIT") {
			fetchAndApplyToken(camera.player?.playerSessionUrl, camera.id);
		}
	};

	const unregisterTokenSetter = (id, camera) => {
		if (camera.player?.playerSessionUrl) {
			delete urlTokenSetters[camera.player.playerSessionUrl].registrations[id];
		} else {
			delete svcTokenSetters[camera.id].registrations[id];
		}
	};

	const get16by9Dimensions = (width, height) => {
		// Check for valid width and height
		if (typeof width !== "number" || width <= 0 || typeof height !== "number" || height <= 0) {
			console.error(`Invalid dimensions provided (${width}, ${height}). Must be a positive number.`);
			return null;
		}

		const aspectRatioHeight = 9;
		const aspectRatioWidth = 16;

		let calculatedWidth = width;
		let calculatedHeight = Math.floor((calculatedWidth * aspectRatioHeight) / aspectRatioWidth);

		if (calculatedHeight > height) {
			calculatedHeight = height;
			calculatedWidth = Math.floor((calculatedHeight * aspectRatioWidth) / aspectRatioHeight);
		}

		return {
			width: calculatedWidth,
			height: calculatedHeight
		};
	};

	return {
		registerTokenSetter,
		unregisterTokenSetter,
		get16by9Dimensions
	};
})();

export default utils;
